import { getInstanceDefinition } from '../cards/cardLookup';
import { getPlayerById } from '../state/selectors';
import { getFamiliarHandler } from '../cards/effectRegistry/familiars';
import { PHASES } from '../constants';

function usageKey(instanceId, timing, abilityIndex) {
  return `${instanceId}:${timing}:${abilityIndex}`;
}

function updatePlayer(state, playerId, updateFn) {
  return { ...state, players: state.players.map((p) => (p.id === playerId ? updateFn(p) : p)) };
}

// Rulebook, "Familiars": "Once per game, as a Spoils ability after defeating
// a monster, you may choose to draw a familiar from the top of the familiar
// deck." Not tied to any specific card's printed Spoils text - every player
// gets this option, once, so it's its own action rather than routed through
// useHandCardAbility (which requires a hand card to key off of).
export function takeFamiliar(state, { playerId }) {
  if (!state.meta.spoilsWindowOpen) throw new Error('You may only take a familiar right after winning a fight.');
  const player = getPlayerById(state, playerId);
  if (player.familiarInstanceId) throw new Error('You have already taken a familiar this game.');
  const [drawnId, ...restDeck] = state.village.familiarDeck;
  if (!drawnId) throw new Error('No familiars remain in the deck.');
  return updatePlayer(
    { ...state, village: { ...state.village, familiarDeck: restDeck } },
    playerId,
    (p) => ({
      ...p,
      familiarInstanceId: drawnId,
      inPlayInstanceIds: [...p.inPlayInstanceIds, drawnId],
      stats: { ...p.stats, familiarTaken: true },
    }),
  );
}

// Rulebook, "Familiars": each ability has an XP score; a player needs that
// much *available* XP to use it (XP is never spent), abilities aren't
// restricted to once-per-game the way taking the familiar itself is, and any
// number of a familiar's distinct abilities can be used in the same turn -
// but the standard "once per card-ability per turn" cap (usedAbilityInstanceIds,
// same as every other hand-card ability) still applies to each one
// individually, since nothing in the familiar rules exempts it from that.
export function useFamiliarAbility(state, { playerId, abilityIndex, choices }) {
  const player = getPlayerById(state, playerId);
  if (!player.familiarInstanceId || !player.inPlayInstanceIds.includes(player.familiarInstanceId)) {
    throw new Error('No familiar available to use right now.');
  }
  const def = getInstanceDefinition(state, player.familiarInstanceId);
  const ability = (def.familiarAbilities || [])[abilityIndex];
  if (!ability) throw new Error(`${def.title} has no ability at index ${abilityIndex}.`);

  const requiredPhase = ability.timing === 'village' ? PHASES.VILLAGE : PHASES.DUNGEON;
  if (state.meta.phase !== requiredPhase) {
    throw new Error(`${def.title}'s ${ability.timing} ability can't be used right now.`);
  }
  if (player.xp < ability.xpCost) {
    throw new Error(`${def.title}'s ability needs ${ability.xpCost} XP available (you have ${player.xp}).`);
  }
  const key = usageKey(player.familiarInstanceId, `familiar-${ability.timing}`, abilityIndex);
  if (player.turnFlags.usedAbilityInstanceIds.includes(key)) {
    throw new Error(`${def.title}'s ability has already been used this turn.`);
  }
  const handler = getFamiliarHandler(def.id, abilityIndex);
  if (!handler) throw new Error(`${def.title}'s ability ("${ability.text}") isn't implemented yet.`);

  let nextState = handler(state, { playerId, instanceId: player.familiarInstanceId, choices: choices || {} });
  nextState = updatePlayer(nextState, playerId, (p) => ({
    ...p,
    turnFlags: {
      ...p.turnFlags,
      usedAbilityInstanceIds: [...p.turnFlags.usedAbilityInstanceIds, key],
      familiarUsedThisTurn: true,
    },
    stats: { ...p.stats, familiarAbilitiesUsed: p.stats.familiarAbilitiesUsed + 1 },
  }));
  return nextState;
}
